import React from 'react';

import useIsMinBreakpoint from '@hero/react-hooks/mediaQuery/useIsMinBreakpoint';
import MembersSparklingIllustration from '@hero/ui-kit/graphics/Illustrations/MembersSparklingIllustration';
import Section from '@hero/ui-kit/layout/Section';
import LimitItems from '../../../components/LimitItems';
import Error from '../../../components/States/Error';
import NoResults from '../../../components/States/NoResults';
import Table from '../../../components/Table';
import MembersToolbar from '../OrganizationMemberList/MembersToolbar';
import useColumnVisibility from '../../../hooks/useColumnVisibility';
import { buildColumnOptions, buildGridTemplate } from '../../../utils/tableColumns';
import buildHeaderColumns from '../../../components/SortBy/buildHeaderColumns';
import useListQueryParams from '../OrganizationMemberList/useListQueryParams';
import { memberSortDefaultDir } from '../OrganizationMemberList/utils/memberSort';
import { OrganizationMemberListPaginated } from '../types';
import {
    DISENGAGED_COLUMNS,
    DISENGAGED_COLUMNS_STORAGE_KEY,
    DISENGAGED_TRAILING_TRACK,
    DisengagedColumnId
} from './columns';
import ListItem from './ListItem';

const DisengagedMemberList: React.FC<{
    isLoading: boolean;
    isError: boolean;
    data?: OrganizationMemberListPaginated;
}> = ({ isLoading, isError, data }) => {
    const isMinL = useIsMinBreakpoint('l');

    const { query } = useListQueryParams();
    const { limit, offset } = query;
    const search = query?.search || '';
    const hasFilters = !!query.device_status || !!query.is_online;

    const { isVisible, toggle } = useColumnVisibility(
        DISENGAGED_COLUMNS_STORAGE_KEY,
        DISENGAGED_COLUMNS
    );

    const gridTemplate = buildGridTemplate(DISENGAGED_COLUMNS, isVisible, {
        collapsed: !isMinL,
        trailingTracks: [DISENGAGED_TRAILING_TRACK]
    });
    const headerColumns = React.useMemo(
        () => buildHeaderColumns(DISENGAGED_COLUMNS, isVisible, memberSortDefaultDir),
        [isVisible]
    );
    const columnControl = React.useMemo(
        () => ({
            options: buildColumnOptions(DISENGAGED_COLUMNS, isVisible),
            onToggle: (id: string) => toggle(id as DisengagedColumnId)
        }),
        [isVisible, toggle]
    );

    const filters = React.useMemo(() => {
        return (
            <MembersToolbar>
                <LimitItems
                    limit={limit ? +limit : 10}
                    offset={offset ? +offset : 0}
                    total_count={data?.total_count}
                    isLoading={isLoading}
                    columnControl={columnControl}
                />
            </MembersToolbar>
        );
    }, [limit, offset, data?.total_count, isLoading, columnControl]);

    const body = React.useMemo(() => {
        if (isError) {
            return <Error />;
        }

        if (data?.list.length === 0 && search.length === 0) {
            return (
                <Section centered>
                    <h5>No disengaged members</h5>
                    <MembersSparklingIllustration
                        styles={{ height: '20rem', width: '64rem', marginTop: '5rem' }}
                    />
                </Section>
            );
        }

        if (data?.list.length === 0 && search.length) {
            return <NoResults search={search} />;
        }

        return (
            <Table
                pagerData={data}
                headerColumns={headerColumns}
                headerGridTemplateColumns={gridTemplate}
                loading={isLoading}
                gridColumnGap="small"
            >
                {data?.list.map((item, index) => (
                    <ListItem
                        key={`${index}-${item.hero_user_id || item.member_id}-${
                            item.email || 'n/a'
                        }`}
                        data={item}
                        search={search}
                        listQuery={query}
                        isColumnVisible={isVisible}
                        gridColumns={gridTemplate}
                    />
                ))}
            </Table>
        );
    }, [
        isError,
        search,
        hasFilters,
        data,
        isLoading,
        query,
        headerColumns,
        gridTemplate,
        isVisible
    ]);

    return (
        <>
            {filters}
            {body}
        </>
    );
};

export default DisengagedMemberList;
